import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useSucursales } from "../lib/useSucursales";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import api, { apiErr, API } from "../lib/api";
import { formatGs } from "../lib/format";
import {
  TrendingUp, TrendingDown, Package, Users, CreditCard, CheckCheck,
  Plus, Wallet, Download, Loader2, Camera, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { GastoModal } from "../components/GastoModal";
import { toast } from "sonner";

const CATEGORIAS = ["Combustible", "Mantenimiento", "Alquiler", "Servicios", "Sueldos varios", "Insumos", "Otros"];

export default function Finanzas() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const SUCS = [{ code: "todas", name: "Todas" }, ...useSucursales().map((s) => ({ code: s.code, name: s.code }))];
  const isAdmin = user?.role === "admin";
  const [suc, setSuc] = useState("todas");
  const [periodo, setPeriodo] = useState("mes");
  const [kpis, setKpis] = useState(null);
  const [showGasto, setShowGasto] = useState(false);
  const [showSalario, setShowSalario] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);

  const load = useCallback(() => {
    api.get(`/finanzas/kpis?sucursal=${suc}&periodo=${periodo}`).then(({ data }) => setKpis(data)).catch(() => {});
    if (isAdmin) api.get("/comisiones/solicitudes").then(({ data }) => setSolicitudes(data.filter((s) => s.estado === "pendiente"))).catch(() => {});
  }, [suc, periodo, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const descargarReporte = async () => {
    try {
      const token = localStorage.getItem("pya_token");
      const res = await fetch(`${API}/reportes/semanal?sucursal=${suc}&periodo=${periodo}&dias=3650`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo generar el reporte");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `reporte_semanal_${suc}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch (e) { toast.error(e.message); }
  };

  const aprobarComision = async (sid) => {
    try { await api.post(`/comisiones/solicitudes/${sid}/aprobar`); toast.success("Pago de comisión aprobado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const rechazarComision = async (sid) => {
    try { await api.post(`/comisiones/solicitudes/${sid}/rechazar`); toast.success("Solicitud rechazada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const cards = [
    { label: "Ingresos totales", value: formatGs(kpis?.ingresos || 0), icon: TrendingUp, color: "text-emerald-600" },
    { label: "Ingresos (flete)", value: formatGs(kpis?.ingresos_flete || 0), icon: TrendingUp, color: "text-emerald-600" },
    { label: "Gastos totales", value: formatGs(kpis?.gastos_totales || 0), icon: TrendingDown, color: "text-rose-500" },
    { label: "Operativos", value: formatGs(kpis?.gastos_operativos || 0), icon: Package, color: "text-[#0A1F44]" },
    { label: "Salarios", value: formatGs(kpis?.salarios || 0), icon: Users, color: "text-[#0A1F44]" },
    { label: "Comisiones pagadas", value: formatGs(kpis?.comisiones_pagadas || 0), icon: Wallet, color: "text-[#F5A623]" },
    { label: "Crédito por cobrar", value: formatGs(kpis?.credito_por_cobrar || 0), icon: CreditCard, color: "text-[#F5A623]" },
    { label: "Crédito cobrado", value: formatGs(kpis?.credito_cobrado || 0), icon: CheckCheck, color: "text-emerald-600" },
  ];

  return (
    <>
      <Header title="Finanzas" subtitle="Números reales de la empresa" onMenu={() => setMobileOpen(true)}
        action={
          <button onClick={descargarReporte} data-testid="download-reporte-btn"
            className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Reporte</span>
          </button>
        } />
      <main className="p-4 sm:p-6 lg:p-8 space-y-5 pb-28">
        {/* Sucursal chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
          {SUCS.map((s) => (
            <button key={s.code} onClick={() => setSuc(s.code)} data-testid={`finanzas-suc-${s.code}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                suc === s.code ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-200 hover:bg-slate-50"}`}>
              {s.code === "todas" ? "🌐 Todas" : s.name}
            </button>
          ))}
        </div>

        {/* Período */}
        <div className="flex gap-2" data-testid="finanzas-periodo">
          {[["dia", "Hoy"], ["semana", "Últimos 7 días"], ["mes", "Este mes"], ["todo", "Todo"]].map(([id, l]) => (
            <button key={id} onClick={() => setPeriodo(id)} data-testid={`finanzas-periodo-${id}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold border ${periodo === id ? "bg-[#F5A623] text-[#0A1F44] border-[#F5A623]" : "bg-white text-[#0A1F44] border-slate-200"}`}>{l}</button>
          ))}
        </div>

        {/* Envíos del día + totales */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-emerald-600 rounded-2xl p-5 shadow-sm text-white" data-testid="kpi-ingresos-hoy">
            <TrendingUp className="h-6 w-6 text-white" />
            <div className="text-2xl font-extrabold tabular-nums mt-3">{formatGs(kpis?.ingresos_hoy || 0)}</div>
            <div className="text-sm text-emerald-50 mt-0.5">Ingresos del día{suc === "todas" ? " (global)" : ` · ${suc}`}</div>
          </div>
          <div className="bg-[#0A1F44] rounded-2xl p-5 shadow-sm text-white" data-testid="kpi-envios-hoy">
            <Package className="h-6 w-6 text-[#F5A623]" />
            <div className="text-3xl font-extrabold tabular-nums mt-3">{kpis?.envios_hoy ?? 0}</div>
            <div className="text-sm text-slate-300 mt-0.5">Envíos del día{suc === "todas" ? " (global)" : ` · ${suc}`}</div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-sm" data-testid="kpi-total-envios">
            <Package className="h-6 w-6 text-[#0A1F44]" />
            <div className="text-3xl font-extrabold text-[#0A1F44] tabular-nums mt-3">{kpis?.total_envios ?? 0}</div>
            <div className="text-sm text-[#64748B] mt-0.5">Envíos del período{suc === "todas" ? " (global)" : ` · ${suc}`}</div>
          </div>
        </div>

        {/* Utilidad destacada */}
        <div data-testid="kpi-utilidad" className="bg-white rounded-2xl border-2 border-emerald-400 p-6 text-center shadow-sm">
          <div className="text-sm font-medium text-[#64748B]">Utilidad</div>
          <div className={`text-5xl font-extrabold tabular-nums mt-1 ${(kpis?.utilidad || 0) < 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {formatGs(kpis?.utilidad || 0)}
          </div>
          <div className="text-sm text-[#64748B] mt-1">Ingresos – Gastos</div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`kpi-${c.label}`}>
                <Icon className={`h-6 w-6 ${c.color}`} />
                <div className="text-2xl font-extrabold text-[#0A1F44] tabular-nums mt-3">{c.value}</div>
                <div className="text-sm text-[#64748B] mt-0.5">{c.label}</div>
              </div>
            );
          })}
        </div>

        <p className="text-sm text-[#64748B] leading-relaxed">
          Utilidad del período = Ingresos (flete + seguro + crédito cobrado) − Gastos. Reglas por sucursal: contado/envío pagado y crédito cobrado cuentan en la sucursal de origen; COD y cobro en destino cuentan en la sucursal de destino al entregarse. Así la suma de las sucursales coincide con el total general (salvo gastos cargados sin sucursal). Crédito por cobrar es el saldo total pendiente.
        </p>

        {/* Solicitudes de comisión (admin) */}
        {isAdmin && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="solicitudes-comision">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-[#0A1F44] flex items-center justify-between">
              <span>Solicitudes de pago de comisión</span>
              <span className="text-xs font-bold text-white bg-[#F5A623] rounded-full px-2 py-0.5">{solicitudes.length}</span>
            </div>
            {solicitudes.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#64748B]">No hay solicitudes pendientes.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {solicitudes.map((s) => (
                  <div key={s.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`solicitud-${s.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0A1F44] truncate">{s.nombre}</div>
                      <div className="text-xs text-[#64748B]">Período {s.periodo} · {s.sucursal || "—"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-extrabold text-[#0A1F44]">{formatGs(s.monto)}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => aprobarComision(s.id)} data-testid={`aprobar-comision-${s.id}`}
                        className="text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-3 py-2">Pagar</button>
                      <button onClick={() => rechazarComision(s.id)} data-testid={`rechazar-comision-${s.id}`}
                        className="text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg px-3 py-2">Rechazar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom actions */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white/95 backdrop-blur border-t border-[#E2E8F0] p-3 flex gap-3 z-20">
        <button onClick={() => setShowGasto(true)} data-testid="registrar-gasto-btn"
          className="flex-1 flex items-center justify-center gap-2 text-base font-bold bg-[#F5A623] hover:bg-[#e0961a] text-[#0A1F44] rounded-xl py-3.5">
          <Plus className="h-5 w-5" /> Registrar gasto
        </button>
        {isAdmin && (
          <button onClick={() => setShowSalario(true)} data-testid="pagar-salario-btn"
            className="flex-1 flex items-center justify-center gap-2 text-base font-bold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-xl py-3.5">
            <Wallet className="h-5 w-5" /> Pagar salario
          </button>
        )}
      </div>

      <GastoModal open={showGasto} onClose={() => setShowGasto(false)} isAdmin={isAdmin} onSaved={load} />
      {isAdmin && <SalarioModal open={showSalario} onClose={() => setShowSalario(false)} onSaved={load} />}
    </>
  );
}

const input = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

function SalarioModal({ open, onClose, onSaved }) {
  const [elegibles, setElegibles] = useState([]);
  const [form, setForm] = useState({ user_id: "", monto: "", nota: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/salarios/elegibles").then(({ data }) => setElegibles(data)).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id) return toast.error("Elegí a quién pagar");
    setSaving(true);
    try {
      await api.post("/salarios", { user_id: form.user_id, monto: Number(form.monto), nota: form.nota });
      toast.success("Salario pagado");
      setForm({ user_id: "", monto: "", nota: "" });
      onSaved(); onClose();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Pagar salario</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-[#64748B]">Solo repartidores y encargados con sueldo (no comisionistas).</p>
          <select className={input} value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="salario-user" required>
            <option value="">Seleccionar empleado…</option>
            {elegibles.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role === "repartidor" ? "Repartidor" : "Encargado"}{u.sucursal ? ` (${u.sucursal})` : ""}</option>)}
          </select>
          {elegibles.length === 0 && <p className="text-xs text-rose-600">No hay empleados con sueldo. Asigná "Salario" en Usuarios y Roles.</p>}
          <input className={input} type="number" min="1" placeholder="Monto (Gs.)" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required data-testid="salario-monto" />
          <input className={input} placeholder="Nota (opcional, ej. sueldo junio)" value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} data-testid="salario-nota" />
          <button disabled={saving} className="w-full rounded-lg bg-[#0A1F44] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="salario-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Registrar pago
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
